/**
 * Category Management - 类目管理（GF / HY 双 Tab 树表格展示）
 * 通过 Tab 切换 GF 和 HY 两套类目体系，每个 Tab 下展示独立的类目树
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Table, Tabs, message, Button, Space } from 'antd';
import { MinusSquareOutlined, PlusSquareOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useTableScrollHeight } from '@/hooks/useTableScrollHeight';
import type { CategoryTreeNode, CategoryType } from '../../../../types/master-data/category';
import * as categoryService from '../../../../services/master-data/categoryService';
import EllipsisCell from '@/components/common/EllipsisCell';

/** 将类目树节点转为 Table 需要的树形数据格式，Encoding 列展示 categoryCode */
const convertToTableData = (nodes: CategoryTreeNode[]): any[] => {
  return nodes.map((node) => ({
    key: node.encoding,
    name: node.name,
    encoding: node.encoding,
    remarks: node.remarks,
    level: node.level,
    children: node.children && node.children.length > 0
      ? convertToTableData(node.children)
      : undefined,
  }));
};

/** 递归收集所有节点 key */
const getAllKeys = (nodes: any[]): React.Key[] => {
  const keys: React.Key[] = [];
  const traverse = (list: any[]) => {
    list.forEach((node) => {
      keys.push(node.key);
      if (node.children) {
        traverse(node.children);
      }
    });
  };
  traverse(nodes);
  return keys;
};

const CategoryList: React.FC = () => {
  const { t } = useTranslation();
  const [activeDimension, setActiveDimension] = useState<'GF' | 'HY'>('GF');
  const [activeTab, setActiveTab] = useState<CategoryType>('GF');
  const [loading, setLoading] = useState(false);
  const [treeData, setTreeData] = useState<any[]>([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  // Table body scroll height (auto-fill remaining space; tree table has no pagination)
  const { tableWrapRef, scrollY } = useTableScrollHeight({ hasPagination: false });

  // 按维度加载类目树
  const loadCategoryTree = useCallback(async (dimension: 'GF' | 'HY') => {
    setLoading(true);
    try {
      const response = await categoryService.getCategoryTree(dimension);
      if (response.success && response.data) {
        const converted = convertToTableData(response.data);
        setTreeData(converted);
        setExpandedRowKeys(converted.map((n: any) => n.key));
      } else {
        message.error(response.errorMessage || t('common.message.loadFailed'));
      }
    } catch {
      message.error(t('common.message.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadCategoryTree(activeDimension);
  }, [activeDimension]);

  const handleTabChange = (key: string) => {
    setActiveDimension(key as 'GF' | 'HY');
  };

  const handleExpandAll = () => {
    setExpandedRowKeys(getAllKeys(treeData));
  };

  const handleCollapseAll = () => {
    setExpandedRowKeys([]);
  };

  const columns = [
    {
      title: t('masterData.category.name'),
      dataIndex: 'name',
      key: 'name',
      width: '40%',
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('masterData.category.categoryCode'),
      dataIndex: 'encoding',
      key: 'encoding',
      width: '30%',
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('masterData.category.remarks'),
      dataIndex: 'remarks',
      key: 'remarks',
      width: '30%',
      render: (val: string) => <EllipsisCell>{val || '-'}</EllipsisCell>,
    },
  ];

  return (
    <div style={{ padding: '16px', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* GF / HY Tab 切换 */}
      <Tabs
        activeKey={activeDimension}
        onChange={handleTabChange}
        items={[
          { key: 'GF', label: t('masterData.category.gfCategory') },
          { key: 'HY', label: t('masterData.category.hyCategory') },
        ]}
        style={{ marginBottom: 8 }}
      />

      {/* 操作栏 */}
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space>
          <Button size="small" icon={<PlusSquareOutlined />} onClick={handleExpandAll}>
            {t('masterData.category.expandAll')}
          </Button>
          <Button size="small" icon={<MinusSquareOutlined />} onClick={handleCollapseAll}>
            {t('masterData.category.collapseAll')}
          </Button>
        </Space>
        <Button icon={<ReloadOutlined />} onClick={() => loadCategoryTree(activeDimension)}>
          {t('masterData.category.refresh')}
        </Button>
      </div>

      {/* 树表格 */}
      <div ref={tableWrapRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Table
          rowKey="key"
          columns={columns}
          dataSource={treeData}
          loading={loading}
          pagination={false}
          expandable={{
            expandedRowKeys,
            onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as React.Key[]),
          }}
          scroll={{ y: scrollY }}
          size="middle"
        />
      </div>
    </div>
  );
};

export default CategoryList;
